import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ControlButton, TscButtonType} from '@techsmith/tsc-cloud-style-guide';
import mediaApi from '../../service/mediaApi';
import {StyledWarning, MinWidthButtonWrapper} from '../util/StyledElements';
import '../../static/css/modal.less';
import {IWorkerState} from '../../model/workerState';
import cssConstants from '../../constants/cssConstants';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {useTscTheme} from '../../context/themeProvider';
import {mediaStore} from '../../context/mediaProvider';
import {BasicModalPortal} from '../util/ModalPortalHelper';
import {importMediaInterface} from '../../hooks/dynamicImport';

export const SetThumbnailModalBase: React.FC<ISetThumbnailModalProps & IStateMappedProps> = ({onClose, Hash}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [setThumbnailState, updateSetThumbnailState] = useState({
      isVisible: true,
      isWorking: true,
      hasError: false
   } as ISetThumbnailState);

   useEffect(() => {
      const updateThumbnail = async () => {
         const mediaInterface = await importMediaInterface();
         try {
            await mediaApi.updateThumbnail(Hash, mediaInterface.currentTime);
            updateSetThumbnailState({
               ...setThumbnailState,
               isVisible: true,
               isWorking: false,
               hasError: false
            });
         } catch {
            updateSetThumbnailState({
               ...setThumbnailState,
               isVisible: true,
               isWorking: false,
               hasError: true
            });
         }
      };

      void updateThumbnail();
   }, []);

   const onHide = () => {
      updateSetThumbnailState({
         ...setThumbnailState,
         isVisible: false
      });
   };

   const onCloseCopy = () => {
      onClose();
   };

   const closeButton = (
      <MinWidthButtonWrapper>
         <ControlButton
            label={t('general.close')}
            buttonType={TscButtonType.primary}
            disabled={false}
            themeName={theme}
            onClick={onCloseCopy}
         />
      </MinWidthButtonWrapper>
   );
   const title = setThumbnailState.isWorking ? t('setThumbnail.working') : setThumbnailState.hasError ? t('setThumbnail.failed') : t('setThumbnail.success.title');

   return (
      <BasicModalPortal
         visible={setThumbnailState.isVisible}
         title={title}
         width={cssConstants.defaultModalWidth}
         onClose={setThumbnailState.isWorking ? onHide : onCloseCopy}
         themeName={theme}
         isLoading={setThumbnailState.isWorking && setThumbnailState.isVisible}
         testId="set-thumbnail-modal"
      >
         <div className="modal-body">
            <div className="form-body themeable-section">
               <div className="form-field-group">
                  {!setThumbnailState.isWorking && !setThumbnailState.hasError && (
                     <span>
                        {t('setThumbnail.success.text')}
                     </span>
                  )}
                  {setThumbnailState.hasError && (
                     <StyledWarning>
                        {t('general.errorText')}
                     </StyledWarning>
                  )}
               </div>
               {!setThumbnailState.isWorking && (
                  <div className="button-group">
                     {closeButton}
                  </div>
               )}
            </div>
         </div>
      </BasicModalPortal>
   );
};

export interface ISetThumbnailModalProps {
   onClose(): void;
}

interface ISetThumbnailState extends IWorkerState {
   isVisible: boolean;
}

export interface IStateMappedProps {
   Hash: string;
}

const mapStatesToProps = (
   {onClose}: ISetThumbnailModalProps,
   {Hash}: Partial<IStateMappedProps>
): IStateMappedProps & ISetThumbnailModalProps => ({onClose, Hash});

export default withMemoizedContexts(mapStatesToProps, mediaStore)(SetThumbnailModalBase);
